import type { RequiredFileModel, Stored, UUID } from "@overmap-ai/common"
import { BaseOfflineFileModelApiService } from "@overmap-ai/core"
import { useVideoViewer } from "@overmap-ai/forms"
import { cx } from "class-variance-authority"
import { useCallback } from "react"

import { useFetchFileFromFileModel } from "../../hooks/utils"
import { AppSDK } from "../../sdk"
import type { RootState } from "../../store"
import { genericMemo } from "../../utils/optimization"
import { VideoFileThumbnail } from "./VideoFileThumbnail"

interface VideoAttachmentThumbnailProps<TAttachment extends UUID<RequiredFileModel>> {
	attachment: Stored<TAttachment>
	service: BaseOfflineFileModelApiService<TAttachment["uuid"], TAttachment, RootState, AppSDK>
}

export const VideoAttachmentThumbnail = genericMemo(function <TAttachment extends UUID<RequiredFileModel>>(
	props: VideoAttachmentThumbnailProps<TAttachment>,
) {
	const { attachment, service } = props

	const [fetchedFile, failed] = useFetchFileFromFileModel(attachment, service)
	const openVideoViewer = useVideoViewer()

	const handleView = useCallback(() => {
		if (!fetchedFile) return

		openVideoViewer((closeVideoViewer) => ({
			file: fetchedFile,
			onClose: closeVideoViewer,
		}))
	}, [fetchedFile, openVideoViewer])

	return (
		<VideoFileThumbnail
			className={cx({
				"cursor-pointer": !!fetchedFile,
			})}
			onClick={handleView}
			file={fetchedFile}
			errored={failed}
		/>
	)
})
